import styled, { css } from 'styled-components';

import { Box } from '@/components/Box';
import { Flex } from '@/components/Flex';
import { LegacyButton } from '@/components/LegacyButton';
import LinkWithLocale from '@/components/LinkWithLocale/LinkWithLocale';
import { Typography } from '@/components/Typography';

export const Break = styled(Box)`
  ${({ theme: { colors, components } }) => css`
    width: 100%;
    height: 0px;
    border: 1px solid ${colors.white};
    opacity: 0.1;
    margin: ${components.footer.break.marginMobile};
  `}
`;

export const FooterVisibility = styled.div<{ $orderMobile?: number }>`
  display: none;
  ${({ $orderMobile, theme: { components } }) => css`
    @media (max-width: ${components.footer.breakpoint}) {
      display: block;
      order: ${$orderMobile ?? 0};
    }
  `}
`;

export const UL = styled.ul``;

export const LI = styled.li`
  display: block;
  width: 100%;
  padding: 4px 0;
`;

const baseFooterLinkStyles = css`
  display: inline-block !important;
  font-family: ${({ theme }) => theme.fonts.paragraphFont};
  font-size: ${({ theme }) => theme.fontSizes.p2};
  font-style: normal;
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  line-height: ${({ theme }) => theme.lineHeights.h5};
  padding-bottom: 2px;
  border-bottom: 2px solid ${({ theme }) => theme.colors.primary};
  -webkit-mask:
    conic-gradient(at bottom 0px left 0px, #0000 -180deg, #000 0) 0
      var(--_i, 200%) / var(--_i, 0px) 200% border-box no-repeat,
    linear-gradient(#000 0 0) padding-box no-repeat;
  transition:
    0.3s,
    -webkit-mask-position 0.3s 0.3s;

  &:hover {
    --_i: 100%;
    transition:
      0s,
      -webkit-mask-size 0.3s 0.3s;
    border-bottom: 2px solid ${({ theme }) => theme.colors.primary};
  }
`;

export const FooterLink = styled(LinkWithLocale)`
  ${baseFooterLinkStyles}
`;

export const FooterLinkButton = styled(LegacyButton)`
  ${baseFooterLinkStyles};
  background: transparent;
  text-transform: capitalize;
  color: ${({ theme }) => theme.colors.white};
  border: none;
  cursor: pointer;
  padding: 0;
  height: unset;
  text-align: center;

  &:hover {
    background-color: transparent;
    color: ${({ theme }) => theme.colors.white};
    border: none;
    border-bottom: 2px solid ${({ theme }) => theme.colors.primary};
  }
`;

export const BottomItemsWrapper = styled.ul`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  ${({ theme }) => css`
    gap: ${theme.spacing(2)} ${theme.spacing(3)};
    ${theme.media.md.down} {
      padding-right: ${theme.spacing(0)};
    }
  `};
  ${({ theme: { colors, components, fontSizes, fonts } }) => css`
    a {
      color: ${colors.white};
      font-family: ${fonts.paragraphFont};
      font-size: ${components.footer.BottomItemsWrapper.fontSize};
      text-decoration: none;
      transition: color 0.2s;
      opacity: 0.76;
      &:hover {
        color: ${components.footer.links.hoverColor};
      }
    }
    margin: ${components.footer.links.margin};
    @media (max-width: ${components.footer.breakpoint}) {
      order: ${components.footer.menus.orderMobile.links};
      a {
        font-size: ${fontSizes.p3};
      }
      margin: ${components.footer.links.marginMobile};
    }
  `}
`;

export const LanguageSelectorWrapper = styled(Box)`
  margin-top: 12px;
  text-align: center;
  position: relative;

  ${({ theme: { components, space } }) => css`
    button {
      width: auto;
      padding: 0 ${space.s100};
      position: absolute;
      left: 0;
    }
    @media (max-width: ${components.footer.breakpoint}) {
      button {
        position: relative;
        padding: 0 ${space.s200};
      }
      margin: 0;
    }
  `}
`;

type GridBoxProps = {
  itemCount: number;
};
export const GridBox = styled(UL)<GridBoxProps>`
  color: ${({ theme }) => theme.colors.white};
  width: 100%;
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
  flex-wrap: wrap;
  & > li {
    flex: 0 0 ${({ itemCount }) => 100 / itemCount}%;
  }
  ${({
    theme: {
      components: {
        footer: { breakpoint },
      },
      space,
    },
  }) => css`
    @media (max-width: ${breakpoint}) {
      display: block;
      padding: 0 16px;
      margin-bottom: ${space.s150};
    }
  `}
`;

export const MenuBox = styled(Box)`
  width: 100%;
  display: block;
`;

export const GroupTitleWrapper = styled(Flex)`
  justify-content: space-between;

  &[type='button'] {
    background: none;
    border: none;
    padding: 0;
    width: 100%;
    text-align: left;
    font: inherit;
    color: inherit;
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid ${({ theme }) => theme.colors.primaryBlue};
      outline-offset: 2px;
    }
  }

  svg {
    margin-top: -5px;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

export const GroupTitle = styled(Typography)`
  cursor: default;
  font-family: ${({ theme }) => theme.fonts.subtitleFont};
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 17.6px;
  text-transform: uppercase;
  margin-bottom: 8px;
`;

export const CountryRegion = styled(GroupTitle)`
  display: block;
  ${({ theme }) => css`
    @media (max-width: ${theme.components.footer.breakpoint}) {
      display: none;
    }
  `}
`;

export const SocialIconBox = styled(Flex)`
  gap: 1rem;
  width: 150px;
  margin-top: 12px;
  margin-bottom: 45px;
  ${({ theme }) => css`
    @media (max-width: ${theme.components.footer.breakpoint}) {
      width: 100%;
      margin-bottom: 24px;
    }
  `}
  a {
    width: 24px;
    min-width: 24px;
    height: 24px;
  }
`;

export const FooterBottomLink = styled(LinkWithLocale)`
  display: inline-block !important;
  font-size: ${({ theme }) =>
    theme.components.footer.BottomItemsWrapper.fontSize} !important;
`;

export const FooterBottomBox = styled(Box)<{
  $footerLinksPosition: 'left' | 'center';
}>`
  display: flex;
  flex-direction: row;
  ${({ $footerLinksPosition }) => css`
    justify-content: ${$footerLinksPosition === 'left'
      ? 'space-between'
      : 'center'};
  `}
  position: relative;
  text-align: center;
  ${({ theme }) => css`
    gap: ${theme.spacing(2)};
    margin: ${theme.components.footer.bottomBox.margin};
    padding: ${theme.components.footer.bottomBox.padding};

    @media (max-width: ${theme.components.footer.breakpoint}) {
      display: flex;
      flex-flow: column;
      gap: 0;
      margin: ${theme.components.footer.bottomBox.marginMobile};
      padding: ${theme.components.footer.bottomBox.paddingMobile};
    }
  `}
`;

export const CopyRight = styled(Typography)<{
  $footerLinksPosition: 'left' | 'center';
}>`
  ${({ $footerLinksPosition }) => css`
    ${$footerLinksPosition === 'center' &&
    css`
      position: absolute;
      right: 0;
      top: 0;
    `}
  `}
  opacity: 0.6;
  white-space: nowrap;
  ${({ theme }) => css`
    color: ${theme.colors.white};
    font-size: ${theme.fontSizes.p3};
    font-family: ${theme.fonts.paragraphFont};
    @media (max-width: ${theme.components.footer.breakpoint}) {
      order: ${theme.components.footer.menus.orderMobile.copyRight};
      position: unset;
      text-align: center;
      margin: -12px auto 5px;
    }
  `}
`;
